/**
 * DashboardSettings — General tab (Iter 191t · Msg 598 cleanup)
 *
 * Post-reorganization, this tab focuses solely on the vendor's public
 * profile and their social-media presence.  The following blocks now live
 * in their own dedicated settings pages:
 *   • Brand identity + Logo → Logo & Favicon
 *   • Tracking Pixels        → Brand & Marketing › Pixels & Tags
 *   • Consent + custom scripts → Permissions › Data & Security
 *   • Privacy & Data actions → Permissions › Privacy & Data
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Save, Link as LinkIcon, Loader2, Upload,
  Instagram, Facebook, Twitter, Youtube, Linkedin, Music2, Plus, Trash2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BackToSettings from './settings/_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

export default function DashboardSettings() {
  const [settings, setSettings] = useState({
    tagline: '',
    social_links: {
      instagram: '', facebook: '', twitter: '', pinterest: '',
      youtube: '', linkedin: '', tiktok: '', custom: [],
    },
  });
  const [profile, setProfile] = useState({
    first_name: '', last_name: '', display_name: '',
    bio: '', website: '', profile_photo_url: '', email: '',
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const photoInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings`)
      .catch(() => ({ data: {} }))
      .then((settingsRes) => {
        setSettings((prev) => {
          const merged = { ...prev, ...settingsRes.data };
          const sl = { ...(prev.social_links || {}), ...(settingsRes.data?.social_links || {}) };
          if (!Array.isArray(sl.custom)) sl.custom = [];
          merged.social_links = sl;
          return merged;
        });
      })
      .finally(() => setLoading(false));

    axios.get(`${API}/auth/me`, { headers })
      .then(({ data }) => {
        setProfile({
          first_name: data?.first_name || '',
          last_name:  data?.last_name || '',
          display_name: data?.display_name || data?.name || '',
          bio:        data?.bio || '',
          website:    data?.website || '',
          profile_photo_url: data?.profile_photo_url || '',
          email:      data?.email || '',
        });
      })
      .catch(() => { /* silent */ });
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, profile, { headers });
      setProfile((p) => ({
        ...p,
        first_name: data?.first_name ?? p.first_name,
        last_name:  data?.last_name  ?? p.last_name,
        display_name: data?.display_name ?? p.display_name,
        bio:        data?.bio        ?? p.bio,
        website:    data?.website    ?? p.website,
        profile_photo_url: data?.profile_photo_url ?? p.profile_photo_url,
      }));
      toast.success('Profile saved');
    } catch { toast.error('Could not save profile'); }
    finally { setSavingProfile(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/media/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const url = data?.url || data?.media_url;
      if (url) {
        await axios.put(`${API}/auth/profile`, { profile_photo_url: url }, { headers });
        setProfile((p) => ({ ...p, profile_photo_url: url }));
        toast.success('Photo updated');
      }
    } catch { toast.error('Photo upload failed'); }
    finally { setUploadingPhoto(false); }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social_')) {
      const socialKey = name.replace('social_', '');
      setSettings((prev) => ({ ...prev, social_links: { ...prev.social_links, [socialKey]: value } }));
    } else {
      setSettings((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers });
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="dashboard-settings">
      <BackToSettings />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="settings-title">General</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Your public profile and social media presence.</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all" data-testid="save-settings-btn">
          <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Profile card */}
      <GlassCard className="p-6" data-testid="profile-card">
        <div className="flex items-start gap-5 flex-col sm:flex-row">
          <div className="relative shrink-0">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-[#A78BFA]/30 to-[#6D28D9]/20 border-2 border-white/10 grid place-items-center">
              {profile.profile_photo_url ? (
                <img src={profile.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-black text-white/60">{(profile.display_name || profile.first_name || profile.email || '?').slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#A78BFA] text-[#0E0E23] shadow-lg flex items-center justify-center hover:bg-white transition-colors disabled:opacity-50"
              title="Edit photo"
              data-testid="profile-photo-edit"
            >
              {uploadingPhoto ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            </button>
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>

          <div className="flex-1 w-full min-w-0">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="font-semibold text-white text-base">Profile</h2>
                <p className="text-[11.5px] text-slate-500 mt-0.5">Your public info shown to buyers and on invoices.</p>
              </div>
              <button
                type="button" onClick={saveProfile} disabled={savingProfile}
                className="px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] bg-[#A78BFA] text-[#0E0E23] hover:bg-white disabled:opacity-50"
                data-testid="profile-save-btn"
              >
                {savingProfile ? 'Saving…' : 'Save profile'}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">First name</Label>
                <Input value={profile.first_name} onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))} placeholder="First name" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="profile-first-name" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Last name</Label>
                <Input value={profile.last_name} onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))} placeholder="Last name" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="profile-last-name" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#94A3B8] text-xs">Brand name</Label>
                <Input value={profile.display_name} onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))} placeholder="Shown to buyers (e.g. Sarah's Studio)" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="profile-display-name" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#94A3B8] text-xs">Website</Label>
                <Input value={profile.website} onChange={(e) => setProfile((p) => ({ ...p, website: e.target.value }))} placeholder="https://yoursite.com" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="profile-website" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#94A3B8] text-xs">Bio</Label>
                <Textarea value={profile.bio} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} placeholder="Tell buyers a bit about yourself — where you're from, what you make, what inspires your work." rows={3} className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl resize-y" data-testid="profile-bio" />
              </div>
              {profile.email && (
                <div className="sm:col-span-2 text-[11px] text-white/40">
                  Signed in as <span className="text-white/75">{profile.email}</span>.{' '}
                  Change email &amp; password in <a href="/website-builder/settings/privacy" className="text-[#A78BFA] hover:underline">Privacy &amp; Data</a>.
                </div>
              )}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Social Media Links */}
      <GlassCard className="p-6" data-testid="social-media-links-section">
        <div className="flex items-center gap-2 mb-1">
          <LinkIcon size={16} className="text-[#A78BFA]" />
          <h2 className="font-semibold text-white">Social Media Links</h2>
        </div>
        <p className="text-[11px] text-slate-500 mb-5">
          These links power your header, footer, and share buttons. Leave any blank to hide it.
        </p>

        <div className="space-y-4">
          {[
            { id: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourbrand', Icon: Instagram },
            { id: 'facebook',  label: 'Facebook',  placeholder: 'https://facebook.com/yourbrand',  Icon: Facebook  },
            { id: 'twitter',   label: 'X (Twitter)', placeholder: 'https://x.com/yourbrand',       Icon: Twitter   },
            { id: 'youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@yourbrand',  Icon: Youtube   },
            { id: 'linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/company/yourbrand', Icon: Linkedin },
            { id: 'tiktok',    label: 'TikTok',    placeholder: 'https://tiktok.com/@yourbrand',   Icon: Music2    },
            { id: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/yourbrand', Icon: LinkIcon  },
          ].map(({ id, label, placeholder, Icon }) => (
            <div key={id}>
              <Label htmlFor={`social_${id}`} className="text-[#94A3B8] text-sm flex items-center gap-1.5">
                <Icon size={12} className="text-[#A78BFA]" /> {label}
              </Label>
              <Input
                id={`social_${id}`}
                name={`social_${id}`}
                value={settings.social_links?.[id] || ''}
                onChange={handleInputChange}
                placeholder={placeholder}
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                data-testid={`${id}-input`}
              />
            </div>
          ))}
        </div>

        {/* Custom links */}
        <div className="mt-6 pt-6 border-t border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[13px] font-semibold text-white">Custom links</div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Add anything not listed above — Behance, Substack, Bandcamp, your Etsy shop, a booking page, etc.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSettings((prev) => ({
                  ...prev,
                  social_links: {
                    ...prev.social_links,
                    custom: [...(prev.social_links?.custom || []), { title: '', url: '' }],
                  },
                }));
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white transition-colors"
              data-testid="add-custom-social-btn"
            >
              <Plus size={12} /> Add
            </button>
          </div>

          {(settings.social_links?.custom || []).length === 0 && (
            <div className="text-[11px] text-[#64748B] italic py-3">
              No custom links yet. Click &quot;Add&quot; to include one.
            </div>
          )}

          <div className="space-y-2">
            {(settings.social_links?.custom || []).map((entry, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center"
                data-testid={`custom-social-row-${idx}`}
              >
                <Input
                  value={entry.title || ''}
                  onChange={(e) => {
                    const next = [...(settings.social_links?.custom || [])];
                    next[idx] = { ...next[idx], title: e.target.value };
                    setSettings((prev) => ({
                      ...prev,
                      social_links: { ...prev.social_links, custom: next },
                    }));
                  }}
                  placeholder="Label (e.g. Behance)"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                  data-testid={`custom-social-title-${idx}`}
                />
                <Input
                  value={entry.url || ''}
                  onChange={(e) => {
                    const next = [...(settings.social_links?.custom || [])];
                    next[idx] = { ...next[idx], url: e.target.value };
                    setSettings((prev) => ({
                      ...prev,
                      social_links: { ...prev.social_links, custom: next },
                    }));
                  }}
                  placeholder="https://…"
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl"
                  data-testid={`custom-social-url-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = (settings.social_links?.custom || []).filter((_, i) => i !== idx);
                    setSettings((prev) => ({
                      ...prev,
                      social_links: { ...prev.social_links, custom: next },
                    }));
                  }}
                  className="w-9 h-9 grid place-items-center rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
                  title="Remove"
                  data-testid={`custom-social-remove-${idx}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
